import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {testDefinitionsListingURL} from 'sample-tests/network/TestDefinitionDAO';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import SchemaSettingsExportDropdown from 'schemas/SchemaSettingsExportDropdown';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import HorizontalThrobber, {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {TestDefinitionEditorQuery} from './__generated/TestDefinitionEditorQuery';
import {TestDefinitionQuery} from './__generated/TestDefinitionQuery';
import ExistingTestDefinitionEditor from './ExistingTestDefinitionEditor';
import TestDefinitionCreateFlow from './TestDefinitionCreateFlow';

type MaybeInaccessibleTestDefinition = TestDefinitionQuery['testDefinition'];

export type TestDefinitionForEditor = ExtractByTypename<
  MaybeInaccessibleTestDefinition,
  'TestDefinitionNode'
>;

export type TestDefinitionOrgWithDefaults = NonNullable<
  TestDefinitionEditorQuery['currentTenantOrganizations']
>[number];

export const TEST_DEFINITION_EDITOR_QUERY: DocumentNode = gql`
  query TestDefinitionEditorQuery @skipAudit @engComponent(name: SAMPLE_TESTS) {
    currentTenantOrganizations {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
`;

export const TEST_DEFINITION_QUERY: DocumentNode = gql`
  query TestDefinitionQuery($testDefinitionId: StrictString!) @skipAudit @engComponent(name: SAMPLE_TESTS) {
    testDefinition(id: $testDefinitionId) {
      ... on TestDefinitionNode {
        id
        name
        description
        organization {
          ... on OrganizationNode {
            id
            name
          }
        }
        resultSchemaCorrect {
          ... on ResultSchemaNode {
            id
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        ...CoreObjectArchivableGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('CoreObjectArchivable').fragment}
`;

interface TestDefinitionEditorProps {
  testDefinition?: TestDefinitionForEditor;
  organizations: Array<TestDefinitionOrgWithDefaults>;
  refetch: () => void;
}

export const TestDefinitionEditor = (props: TestDefinitionEditorProps): JSX.Element => {
  const ownerChoices = getSchemaOwnerChoices();

  if (ownerChoices.length === 0) {
    return <NoOwnersSchemaMessage pluralName="test definitions" />;
  }

  return (
    <SettingsPageContainer
      onClickTitle={() => {
        BenchlingUtil.navigate(null, testDefinitionsListingURL());
      }}
      subtitle={`${props.testDefinition ? 'Edit' : 'Create'} Test Definition`}
      title="Test Definitions"
      toolbar={
        props.testDefinition && (
          <SchemaSettingsExportDropdown
            schemaId={props.testDefinition.id}
            schemaType={ModelTypeConstants.TEST_DEFINITION}
          />
        )
      }
    >
      {props.testDefinition ? (
        <ExistingTestDefinitionEditor
          testDefinition={props.testDefinition}
          ownerChoices={ownerChoices}
          refetch={props.refetch}
        />
      ) : (
        <TestDefinitionCreateFlow
          organizations={props.organizations}
          ownerChoices={ownerChoices}
          schemaPermissions={null}
        />
      )}
    </SettingsPageContainer>
  );
};

interface QueryingTestDefinitionEditorProps {
  testDefinitionId?: string;
}
const QueryingTestDefinitionEditor = ({testDefinitionId}: QueryingTestDefinitionEditorProps): JSX.Element => (
  <BenchlingQuery<TestDefinitionQuery>
    query={TEST_DEFINITION_QUERY}
    renderLoading={() => <CenteredHorizontalThrobber />}
    variables={{testDefinitionId}}
    skip={testDefinitionId == null}
    fetchPolicy="cache-and-network"
  >
    {(data, {refetch: testDefinitionRefetch}) => {
      // BenchlingQuery bug: skippable queries will have "data === undefined"
      const maybeTestDefinition: MaybeInaccessibleTestDefinition | undefined = data?.testDefinition;

      if (isInaccessible(maybeTestDefinition)) {
        notifications.error('You do not have access to this test definition.', {
          id: maybeTestDefinition.invalidId,
        });
        return <HorizontalThrobber />;
      }

      return (
        <BenchlingQuery<TestDefinitionEditorQuery>
          query={TEST_DEFINITION_EDITOR_QUERY}
          renderLoading={() => <CenteredHorizontalThrobber />}
        >
          {({currentTenantOrganizations}, {refetch: testDefinitionEditorRefetch}) => (
            <TestDefinitionEditor
              organizations={currentTenantOrganizations}
              testDefinition={maybeTestDefinition ?? undefined}
              refetch={() => {
                testDefinitionRefetch();
                testDefinitionEditorRefetch();
              }}
            />
          )}
        </BenchlingQuery>
      );
    }}
  </BenchlingQuery>
);
export default QueryingTestDefinitionEditor;
